import React, { useState, useEffect, useRef } from 'react';
import { FaServer, FaUser, FaUsers, FaEdit, FaKey, FaToggleOn, FaToggleOff, FaUserPlus, FaTimes, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { collection, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import styles from '../style/UserManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../components/TableFilter';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filters, setFilters] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalClosing, setModalClosing] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    birthdate: '',
    state: 'inaktif'
  });
  const tableContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [showVerticalScrollIndicator, setShowVerticalScrollIndicator] = useState(false);

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().eName,
        lastName: doc.data().eSurname,
        email: doc.data().eMail,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const activeUsersCount = users.filter(user => user.state === 'aktif').length;

  const toggleStatus = async (id, currentState) => {
    try {
      const newState = currentState === 'aktif' ? 'inaktif' : 'aktif';
      const userDoc = doc(db, 'Employees', id);
      await updateDoc(userDoc, { state: newState });
      setUsers(users.map(user => user.id === id ? { ...user, state: newState } : user));
    } catch (error) {
      console.error('Error updating user state:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const addUserToFirestore = async () => {
    try {
      const employeeData = {
        eName: newUser.firstName,
        eSurname: newUser.lastName,
        eMail: newUser.email,
        phone: newUser.phone,
        role: newUser.role,
        state: newUser.state,
        birthdate: newUser.birthdate
      };

      await addDoc(collection(db, 'Employees'), employeeData);
      fetchUsers();
      toast.success('Yeni kullanıcı başarıyla eklendi!');
      closeModal();
    } catch (error) {
      console.error('Error adding new user:', error);
      toast.error('Yeni kullanıcı eklenemedi!');
    }
  };

  const closeModal = () => {
    setModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setModalClosing(false);
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        birthdate: '',
        state: 'inaktif'
      });
    }, 500);
  };

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);

    let filtered = users;
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        if (key === 'state') {
          filtered = filtered.filter(user => user[key] === value);
        } else {
          filtered = filtered.filter(user =>
            String(user[key])
              .toLowerCase()
              .includes(newFilters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredUsers(filtered);
  };

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  useEffect(() => {
    const checkScroll = () => {
      const container = tableContainerRef.current;
      if (container) {
        const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
        const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
        setShowScrollIndicator(hasHorizontalScroll && !isScrolledToEnd);

        const hasVerticalScroll = container.scrollHeight > container.clientHeight;
        const isScrolledToBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
        setShowVerticalScrollIndicator(hasVerticalScroll && !isScrolledToBottom);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  const scrollRight = () => {
    const container = tableContainerRef.current;
    if (container) {
      container.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };

  const scrollDown = () => {
    const container = tableContainerRef.current;
    if (container) {
      container.scrollBy({
        top: 300,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Kullanıcı Yönetimi</h1>
        <div className={styles.breadcrumb}>Yönetici Menü / Kullanıcı Yönetimi</div>
      </div>

      <div className={styles.cardContainer}>
        <div className={`${styles.card} ${styles.cardOnline}`}>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>Çevrimiçi Kullanıcı</div>
            <div className={styles.cardValue}>---</div>
          </div>
          <FaServer className={styles.cardIcon} />
        </div>
        <div className={`${styles.card} ${styles.cardActive}`}>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>Aktif Kullanıcı</div>
            <div className={styles.cardValue}>{activeUsersCount > 0 ? activeUsersCount : '---'}</div>
          </div>
          <FaUser className={styles.cardIcon} />
        </div>
        <div className={`${styles.card} ${styles.cardTotal}`}>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>Toplam Kullanıcı</div>
            <div className={styles.cardValue}>{users.length > 0 ? users.length : '---'}</div>
          </div>
          <FaUsers className={styles.cardIcon} />
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <div className={styles.tableContainer} style={{
          backgroundColor: '#31373d',
          borderRadius: '8px',
          padding: '1rem'
        }} ref={tableContainerRef}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>Kullanıcılar</h2>
            <div className={styles.tableActions}>
              {Object.keys(filters).length > 0 && (
                <button
                  className={styles.clearFiltersButton}
                  onClick={() => {
                    setFilters({});
                    setFilteredUsers(users);
                  }}
                >
                  <FaTimes /> Tüm Filtreleri Temizle
                </button>
              )}
              <button className={styles.addButton} onClick={() => setIsModalOpen(true)}>
                <FaUserPlus style={{ marginRight: '0.5rem' }} /> Kullanıc Ekle
              </button>
            </div>
          </div>
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeaderRow}>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  İsim Soy İsim
                  <TableFilter column="eName" type="text" onFilter={handleFilter} />
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  Kullanıcı Adı
                  <TableFilter column="eMail" type="text" onFilter={handleFilter} />
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  Rol
                  <TableFilter column="role" type="text" onFilter={handleFilter} />
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  E-posta
                  <TableFilter column="eMail" type="text" onFilter={handleFilter} />
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  Telefon
                  <TableFilter column="phone" type="text" onFilter={handleFilter} />
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>
                  Durum
                  <TableFilter
                    column="state"
                    type="select"
                    options={[
                      { value: "", label: "Tümü" },
                      { value: "aktif", label: "Aktif" },
                      { value: "inaktif", label: "İnaktif" }
                    ]}
                    onFilter={handleFilter}
                  />
                  {/* Add the scroll indicator here */}
                  {showScrollIndicator && (
                    <button
                      className={styles.scrollIndicator}
                      onClick={scrollRight}
                      aria-label="Scroll right"
                    >
                      <FaChevronRight />
                    </button>
                  )}
                </th>
                <th className={styles.tableHeaderCell} style={{
                  backgroundColor: '#23272a',
                  color: '#ffffff',
                  padding: '12px',
                  borderBottom: '1px solid #32363a'
                }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                <>
                  {filteredUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      style={{
                        backgroundColor: index % 2 === 0
                          ? '#2c2f33'
                          : '#3a3f44',
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        color: '#ffffff',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        const overlay = document.createElement('div');
                        overlay.style.position = 'absolute';
                        overlay.style.top = '0';
                        overlay.style.left = '0';
                        overlay.style.width = '100%';
                        overlay.style.height = '100%';
                        overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        overlay.style.pointerEvents = 'none';
                        overlay.className = 'row-overlay';
                        e.currentTarget.appendChild(overlay);
                      }}
                      onMouseLeave={(e) => {
                        const overlay = e.currentTarget.querySelector('.row-overlay');
                        if (overlay) {
                          overlay.remove();
                        }
                      }}
                    >
                      <td className={styles.tableCell}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {user.profileImageUrl ? (
                            <img
                              src={user.profileImageUrl}
                              alt={`${user.eName} ${user.eSurname}`}
                              className={styles.profileImage}
                              style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '0.5rem' }}
                            />
                          ) : (
                            <div className={styles.avatar}>{user.eName.charAt(0)}</div>
                          )}
                          {user.eName} {user.eSurname}
                        </div>
                      </td>
                      <td className={styles.tableCell}>{user.eMail.split('@')[0]}</td>
                      <td className={styles.tableCell}>{user.role || 'N/A'}</td>
                      <td className={styles.tableCell}>{user.eMail}</td>
                      <td className={styles.tableCell}>{user.phone}</td>
                      <td className={styles.tableCell}>
                        <span className={`${styles.statusBadge} ${user.state === 'aktif' ? styles.statusActive : styles.statusInactive}`}>
                          {user.state}
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <button className={`${styles.actionButton} ${styles.editButton}`}><FaEdit /></button>
                        <button className={`${styles.actionButton} ${styles.keyButton}`}><FaKey /></button>
                        <button
                          onClick={() => toggleStatus(user.id, user.state)}
                          className={`${styles.actionButton} ${styles.toggleButton}`}
                        >
                          {user.state === 'aktif' ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 1 && (
                    <tr style={{ height: '53px', backgroundColor: '#3a3f44' }}>
                      <td colSpan="7"></td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '1rem',
                        backgroundColor: '#2c2f33'
                      }}
                    >
                      Kullanıcı verisi bulunamadı
                    </td>
                  </tr>
                  <tr style={{ height: '53px', backgroundColor: '#3a3f44' }}>
                    <td colSpan="7"></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        {showVerticalScrollIndicator && (
          <button
            className={`${styles.scrollIndicator} ${styles.scrollIndicatorVertical}`}
            onClick={scrollDown}
            aria-label="Scroll down"
          >
            <FaChevronDown />
          </button>
        )}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${modalClosing ? styles.modalExit : ''}`}>
            <h2>Yeni Kullanıcı Ekle</h2>
            <form>
              <input
                type="text"
                name="firstName"
                placeholder="İsim"
                value={newUser.firstName}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Soy İsim"
                value={newUser.lastName}
                onChange={handleInputChange}
              />
              <input
                type="email"
                name="email"
                placeholder="E-posta"
                value={newUser.email}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="phone"
                placeholder="Telefon"
                value={newUser.phone}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="role"
                placeholder="Rol"
                value={newUser.role}
                onChange={handleInputChange}
              />
              <input
                type="date"
                name="birthdate"
                placeholder="gg.aa.yyyy"
                value={newUser.birthdate}
                onChange={handleInputChange}
              />
            </form>
            <div className={styles.modalActions}>
              <button onClick={closeModal}>Kapat</button>
              <button onClick={addUserToFirestore} style={{ backgroundColor: "#48CFCB" }}>Ekle</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default UserManagementPage;